/**
 * Styled components for CopyableAmount
 */

import styled from 'styled-components';

/**
 * Container for the amount display
 * Handles hover states and copy success feedback
 */
export const AmountContainer = styled.div<{ $hovered: boolean; $copied: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0.25rem;  /* Improved from 0.1rem for better visual separation */
  position: relative;
  cursor: pointer;
  /* Increased padding for larger hover area (mobile: ~50x45px hit area) */
  padding: 0.625rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.15s ease-out;
  background: ${props =>
    props.$copied
      ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
      : props.$hovered
        ? 'rgba(248, 250, 252, 1)'
        : 'transparent'};
  transform: scale(${props => props.$hovered && !props.$copied ? 1.02 : 1});
  border: 1px solid ${props => props.$copied ? '#86efac' : 'transparent'};

  /* Tablet: increase padding further (~55x50px hit area) */
  @media (min-width: 768px) {
    gap: 0.375rem;  /* Improved inner gap for tablet+ */
    padding: 0.75rem 0.875rem;
  }

  /* Desktop: maximum padding for easy hovering (~60x50px hit area) */
  @media (min-width: 1024px) {
    gap: 0.375rem;  /* Consistent with tablet */
    padding: 0.875rem 1rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

/**
 * Display area for the value
 */
export const AmountDisplay = styled.div`
  position: relative;
  width: 100%;
  min-height: 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/**
 * The value itself (number)
 */
export const AmountValue = styled.span<{ $positive?: boolean; $colorOverride?: string }>`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-size: 0.8rem;
  color: ${props => props.$colorOverride || (props.$positive ? '#059669' : '#1e293b')};
  letter-spacing: -0.01em;
  display: block;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.9rem;
  }
`;

/**
 * The currency label (olv/usd)
 * Explicit font-family ensures Windows compatibility (prevents Thai character substitution)
 * Note: text-transform handled globally in global.css
 */
export const AmountLabel = styled.span`
  font-family: Arial, Helvetica, sans-serif !important;  /* Windows-safe fonts only */
  font-size: 0.65rem;  /* Increased from 0.55rem to prevent Windows rendering issues */
  color: #94a3b8;
  font-weight: 500;
  text-align: center;
  display: block;
  text-transform: none !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  @media (min-width: 768px) {
    font-size: 0.7rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.75rem;
  }
`;

/**
 * Tooltip container with dual-value display
 */
export const DualValueTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 200px;
`;

/**
 * Row for each currency in the tooltip
 */
export const CurrencyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

/**
 * Currency label (olv/usd) in tooltip
 * Explicit font-family ensures Windows compatibility (prevents Thai character substitution)
 * Note: text-transform handled globally in global.css
 */
export const CurrencyLabel = styled.span`
  font-family: Arial, Helvetica, sans-serif !important;  /* Windows-safe fonts only */
  font-size: 0.7rem;  /* Increased for consistent rendering */
  color: #64748b;
  font-weight: 600;
  min-width: 32px;
  text-transform: none !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

/**
 * Currency value display
 */
export const CurrencyValue = styled.span`
  font-size: 0.75rem;
  color: #e2e8f0;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  flex: 1;
  text-align: right;
`;

/**
 * Copy icon button in tooltip
 */
export const CopyIconButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.15);
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.25);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 11px;
    height: 11px;
    color: #6366f1;
  }
`;

/**
 * Divider line in tooltip
 */
export const TooltipDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #475569 50%, transparent 100%);
  margin: 0.125rem 0;
`;

/**
 * Instruction text in tooltip
 */
export const TooltipInstruction = styled.div`
  font-size: 0.6rem;
  color: #94a3b8;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  svg {
    width: 10px;
    height: 10px;
  }
`;
